import {Injectable} from '@angular/core';
import {
  AFFILIATE_CLICK_ID_VALIDITY_PERIOD_MS,
  IUtmParamsCollection,
  LOCAL_STORAGE_TRACKING_PARAMETERS,
  ORGANIC_SOURCES,
  SESSION_TRACKING_PARAMETERS,
  TRACKED_UTM_PARAMS,
  UTM_LOCALSTORAGE_KEY,
  UTM_PARAM_AFFILIATE_CL_ID,
  UTM_PARAM_AFFILIATE_CL_TIMESTAMP,
  UTM_PARAM_CID,
  UTM_PARAM_CURRENT_URL,
  UTM_PARAM_FBC,
  UTM_PARAM_FBP,
  UTM_PARAM_GA_SESSION_ID,
  UTM_PARAM_HUBSPOTUTK,
  UTM_PARAM_MEDIUM,
  UTM_PARAM_MEDIUM_NONE,
  UTM_PARAM_MEDIUM_ORGANIC,
  UTM_PARAM_MEDIUM_REFERRAL,
  UTM_PARAM_MEDIUM_SOCIAL,
  UTM_PARAM_REFERRER,
  UTM_PARAM_SOURCE,
  UTM_PARAM_UAG,
  UTM_PARAM_USER_AGENT,
  UTM_SOURCE_VALUE_DIRECT,
  UTM_SOURCE_VALUES,
} from '../../../common/models/utm';

const UTM_TEMPORARILY_STORAGE_KEY = `${UTM_LOCALSTORAGE_KEY}-temp`;

@Injectable({
  providedIn: 'root',
})
export class UtmService {
  isEmpty(obj: {[key: string]: any}): boolean {
    return Object.keys(obj).length === 0;
  }

  getHost(url: string) {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch (e) {
      console.error(e);
      return '';
    }
  }

  generateUtm(): IUtmParamsCollection {
    const utm: IUtmParamsCollection = {};

    try {
      const ref = window.document.referrer || '';

      if (ref && ref.length > 0) {
        TRACKED_UTM_PARAMS?.some(utmSource => {
          if (ref.toLowerCase().includes(utmSource)) {
            utm[UTM_PARAM_SOURCE] = utmSource;
            utm[UTM_PARAM_MEDIUM] = ORGANIC_SOURCES.includes(utmSource)
              ? UTM_PARAM_MEDIUM_ORGANIC
              : UTM_PARAM_MEDIUM_SOCIAL;
            return true;
          }
          return false;
        });

        if (!utm[UTM_PARAM_SOURCE]) {
          utm[UTM_PARAM_SOURCE] = this.getHost(ref);
          utm[UTM_PARAM_MEDIUM] = UTM_PARAM_MEDIUM_REFERRAL;
        }
      } else {
        utm[UTM_PARAM_SOURCE] = UTM_SOURCE_VALUE_DIRECT;
        utm[UTM_PARAM_MEDIUM] = UTM_PARAM_MEDIUM_NONE;
      }
    } catch (e) {
      console.error('utm generate exception: ', e);
    }

    return utm;
  }

  getUserAgent(): string | null {
    try {
      return window.navigator.userAgent;
    } catch (e) {
      return null;
    }
  }

  resolveAffiliate(
    localStoragePayload: any,
    queryParams: URLSearchParams,
  ): {
    affClickId: string | null;
    affTimestamp: number | null;
  } {
    let affClickId: string | null = null;
    let affTimestamp: number | null = null;

    try {
      if (!localStoragePayload.hasOwnProperty(UTM_PARAM_AFFILIATE_CL_TIMESTAMP)) {
        delete localStoragePayload[UTM_PARAM_AFFILIATE_CL_ID];
      }

      const lsAffClickId = localStoragePayload[UTM_PARAM_AFFILIATE_CL_ID];
      const lsAffTimestamp = localStoragePayload[UTM_PARAM_AFFILIATE_CL_TIMESTAMP];
      const nowTimestamp = new Date().getTime();

      if (!!lsAffClickId && !!lsAffTimestamp && nowTimestamp - lsAffTimestamp < AFFILIATE_CLICK_ID_VALIDITY_PERIOD_MS) {
        return {affClickId: lsAffClickId, affTimestamp: lsAffTimestamp};
      }

      const queryAffClickId = queryParams.get(UTM_PARAM_AFFILIATE_CL_ID);
      const queryAffTimestamp = queryParams.get(UTM_PARAM_AFFILIATE_CL_TIMESTAMP);
      if (!!queryAffClickId && !!queryAffTimestamp) {
        affClickId = queryAffClickId;
        affTimestamp = +queryAffTimestamp * 1000;
      }
    } catch (e) {
      console.error(e);
    }

    return {affClickId, affTimestamp};
  }

  resolveReferrer() {
    const referrer = window.document.referrer;

    if (!referrer) {
      return {
        referrer: null,
      };
    }

    const isInternal = !!referrer && referrer.startsWith('https://userway.org');
    const fromWidget = !!referrer && referrer.startsWith('https://cdn.userway.org');

    // if current referrer is not internal - use it
    if (referrer && !isInternal) {
      return {
        referrer,
        isFromWidgetReferrer: fromWidget,
      };
    }

    // default
    return {
      referrer: null,
    };
  }

  getHubspotUTK() {
    let hubSpotUId = null;
    try {
      const cookieValue = document.cookie.match(/hubspotutk=(.+?);/);

      if (cookieValue && cookieValue[1]) {
        hubSpotUId = cookieValue[1];
      }
    } catch (e) {}

    return hubSpotUId;
  }

  // Get Facebook Pixel ID
  getFbp() {
    let fbp = null;
    try {
      const fbpVal = /_fbp=(fb\.1\.\d+\.\d+)/.exec(window.document.cookie);
      if (fbpVal && fbpVal[1]) {
        fbp = fbpVal[1];
      }
    } catch (e) {
      console.error(e);
    }

    return fbp;
  }

  // Get Facebook Conversion ID
  getFbc() {
    let fbc = null;
    try {
      const fbcVal = /_fbc=(fb\.1\.\d+\.\w+)/.exec(window.document.cookie);
      if (fbcVal && fbcVal[1]) {
        fbc = fbcVal[1];
      }
    } catch (e) {
      console.error(e);
    }

    return fbc;
  }

  // Get GA client id
  getGaCid() {
    let getGaClientId = null;
    try {
      getGaClientId = document.cookie
        .match(/_ga=(.+?);/)[1]
        .split('.')
        .slice(-2)
        .join('.');
    } catch (e) {
      console.warn('ga is absent');
    }
    return getGaClientId;
  }

  getGaSessionId() {
    let getGaClientId = null;
    try {
      const cookieValue = document.cookie.match(/_ga_[A-Z0-9]+=(.+?)+/);

      if (cookieValue && cookieValue[1]) {
        getGaClientId = cookieValue[0].split('.')[2];
      }
    } catch (e) {
      console.error('getGaSessionId', e);
    }
    return getGaClientId;
  }

  setCookiesParams(trackingParams: any) {
    const fbp = this.getFbp();
    if (fbp) {
      trackingParams[UTM_PARAM_FBP] = fbp;
    }

    const fbc = this.getFbc();
    if (fbc) {
      trackingParams[UTM_PARAM_FBC] = fbc;
    }

    const gaCid = this.getGaCid();
    if (gaCid) {
      trackingParams[UTM_PARAM_CID] = gaCid;
    }

    const gaSessionId = this.getGaSessionId();
    if (gaCid) {
      trackingParams[UTM_PARAM_GA_SESSION_ID] = gaSessionId;
    }

    const hUtk = this.getHubspotUTK();
    if (hUtk) {
      trackingParams[UTM_PARAM_HUBSPOTUTK] = hUtk;
    }
  }

  setLocalStorageParams() {
    const thisUrl = window.location.href;
    const PARAMS = LOCAL_STORAGE_TRACKING_PARAMETERS;
    const trackingParams = {};
    const savedParams = this.getSessionStorageSavedTrackingParams();

    const {searchParams} = new URL(thisUrl);

    PARAMS.forEach((p: string) => {
      const queryParamValue = searchParams.has(p) && searchParams.get(p);
      if (queryParamValue && Object.keys(queryParamValue).length > 0) {
        trackingParams[p] = queryParamValue;
      }
    });

    this.setCookiesParams(trackingParams);

    const {affClickId, affTimestamp} = this.resolveAffiliate(savedParams, searchParams);

    delete savedParams[UTM_PARAM_AFFILIATE_CL_ID];
    delete savedParams[UTM_PARAM_AFFILIATE_CL_TIMESTAMP];

    if (affClickId && affTimestamp) {
      trackingParams[UTM_PARAM_AFFILIATE_CL_ID] = affClickId;
      trackingParams[UTM_PARAM_AFFILIATE_CL_TIMESTAMP] = affTimestamp;
    }

    trackingParams[UTM_PARAM_UAG] = this.getUserAgent();
    trackingParams[UTM_PARAM_USER_AGENT] = this.getUserAgent();
    trackingParams[UTM_PARAM_CURRENT_URL] =
      window.location.protocol + '//' + window.location.host + window.location.pathname;

    const lsPayload = Object.assign({}, savedParams, trackingParams);
    const storageValue = JSON.stringify(lsPayload);
    localStorage.setItem(UTM_LOCALSTORAGE_KEY, storageValue);
    localStorage.setItem(UTM_TEMPORARILY_STORAGE_KEY, storageValue);
    return storageValue;
  }

  setSessionStorageParams() {
    const savedParams = this.getSessionStorageSavedTrackingParams();

    // we do not override session storage values
    if (savedParams && Object.keys(savedParams).length > 0) {
      return;
    }

    const {referrer, isFromWidgetReferrer} = this.resolveReferrer();
    const trackingParams = {};
    if (referrer) {
      trackingParams[UTM_PARAM_REFERRER] = referrer;
    }
    const {searchParams} = new URL(window.location.href);

    let urlHasUtmParams = false;
    SESSION_TRACKING_PARAMETERS.forEach((p: string) => {
      const queryParamValue = searchParams.has(p) && searchParams.get(p);
      if (queryParamValue && Object.keys(queryParamValue).length) {
        trackingParams[p] = queryParamValue;
        if (p.startsWith('utm_')) {
          urlHasUtmParams = true;
        }
      }
    });

    if (!urlHasUtmParams) {
      if (referrer) {
        if (isFromWidgetReferrer) {
          // In case the refer is UserWay CDN consider as direct
          // utm_medium: "none"
          // utm_source: "direct"
          // Note, shouldn't happen; since cdn.userway.org resources always include UTM
          trackingParams[UTM_PARAM_MEDIUM] = UTM_PARAM_MEDIUM_NONE;
          trackingParams[UTM_PARAM_SOURCE] = UTM_SOURCE_VALUE_DIRECT;
          this.resetSessionStorageUtmParameters(savedParams);
        } else {
          UTM_SOURCE_VALUES.forEach(p => {
            if (referrer.toLowerCase().indexOf(p) > -1) {
              trackingParams[UTM_PARAM_SOURCE] = p;
              trackingParams[UTM_PARAM_MEDIUM] = ORGANIC_SOURCES.includes(p)
                ? UTM_PARAM_MEDIUM_ORGANIC
                : UTM_PARAM_MEDIUM_SOCIAL;
            }
          });

          if (!trackingParams[UTM_PARAM_SOURCE]) {
            const {hostname} = new URL(referrer);
            trackingParams[UTM_PARAM_SOURCE] = hostname.replace(/(^www\.)/, '');
            trackingParams[UTM_PARAM_MEDIUM] = UTM_PARAM_MEDIUM_REFERRAL;
          }
        }
      } else {
        trackingParams[UTM_PARAM_SOURCE] = UTM_SOURCE_VALUE_DIRECT;
        trackingParams[UTM_PARAM_MEDIUM] = UTM_PARAM_MEDIUM_NONE;
      }
    }
    const storageValue = JSON.stringify(trackingParams);
    sessionStorage.setItem(UTM_LOCALSTORAGE_KEY, storageValue);
    sessionStorage.setItem(UTM_TEMPORARILY_STORAGE_KEY, storageValue);
    return storageValue;
  }

  getSessionStorageSavedTrackingParams() {
    try {
      const existingTrackingParams = sessionStorage.getItem(UTM_LOCALSTORAGE_KEY);
      return !!existingTrackingParams ? JSON.parse(existingTrackingParams) : {};
    } catch (e) {
      return {};
    }
  }

  // not needed; just for synthetic test cases
  resetSessionStorageUtmParameters(obj: any) {
    SESSION_TRACKING_PARAMETERS.forEach((p: string) => {
      delete obj[p];
    });
  }

  getLocalStorageSavedTrackingParams() {
    try {
      const existingTrackingParams = localStorage.getItem(UTM_LOCALSTORAGE_KEY);
      return !!existingTrackingParams ? JSON.parse(existingTrackingParams) : {};
    } catch (e) {
      // TODO: remove when deal with error
      // https://userway.airbrake.io/projects/526264/groups/3784808425192128578
      throw new Error(`utm.service - getLocalStorageSavedTrackingParams - ${e.message}`);
    }
  }
  getAndRemoveUtmIfAny(paramsOverride: any = {}) {
    let trackingParams = {};

    try {
      const localStorageParams = this.getLocalStorageSavedTrackingParams();
      this.setCookiesParams(localStorageParams);

      const sessionStorageParams = this.getSessionStorageSavedTrackingParams();

      trackingParams = {...localStorageParams, ...sessionStorageParams};
      if (!this.isEmpty(paramsOverride) && trackingParams) {
        trackingParams = {
          ...trackingParams,
          ...paramsOverride,
        };
      }

      return trackingParams;
    } catch (e) {
      console.error('getAndRemoveUtmIfAny', e);
      return trackingParams;
    }
  }
  getCookie(name: string) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    if (match) {
      return match[2];
    }
  }
  setUtmIfAny(this: any): Promise<string | null> {
    return new Promise<string | null>((resolve, reject) => {
      try {
        const local_utm_params = this.setLocalStorageParams();
        const session_utm_params = this.setSessionStorageParams();

        const uwPPCSession = this.setUtmCookie();
        const handleUtmData = () => {
          sessionStorage.removeItem(UTM_TEMPORARILY_STORAGE_KEY);
          localStorage.removeItem(UTM_TEMPORARILY_STORAGE_KEY);

          sessionStorage.setItem(UTM_LOCALSTORAGE_KEY, session_utm_params);
          localStorage.setItem(UTM_LOCALSTORAGE_KEY, local_utm_params);
        };

        window.addEventListener('CookieConsentOnDecline', handleUtmData);
        window.addEventListener('CookieConsentOnAccept', handleUtmData);
        resolve(uwPPCSession);
      } catch (err) {
        reject(err);
      }
    });
  }

  setUtmCookie() {
    const uwPPCSession: string | null = sessionStorage.getItem(UTM_LOCALSTORAGE_KEY);
    document.cookie = `uwPPCCookie=${uwPPCSession}; path=/; Secure`;
    return uwPPCSession;
  }
}
